import React, { useEffect, useRef, useState } from 'react';
import { audienceTestimonials, clientTestimonials } from '../data/mockData';
import { Quote } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const TestimonialsCarousel = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const audienceTrackRef = useRef(null);
  const clientTrackRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const sectionElement = sectionRef.current;

    if (!(sectionElement instanceof HTMLElement)) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.2,
        rootMargin: '120px',
      },
    );

    observer.observe(sectionElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isInView || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    let animationFrameId = 0;
    let lastTimestamp = performance.now();

    let audienceOffset = 0;
    let clientOffset = 0;
    let audienceWidth = 0;
    let clientWidth = 0;

    const advance = (element, speed, getOffset, setOffset) => {
      if (!(element instanceof HTMLElement)) return;

      const fullWidth = element.scrollWidth;
      const halfWidth = fullWidth / 2;

      if (fullWidth === 0 || halfWidth === 0) return;

      setOffset((getOffset() + speed) % halfWidth);
      element.style.transform = `translate3d(${-getOffset()}px, 0, 0)`;
    };

    const advanceLeft = (element, speed, getOffset, setOffset) => {
      if (!(element instanceof HTMLElement)) return;

      const fullWidth = element.scrollWidth;
      const halfWidth = fullWidth / 2;

      if (fullWidth === 0 || halfWidth === 0) return;

      const current = getOffset();
      if (current === 0) {
        setOffset(halfWidth);
        element.style.transform = `translate3d(${-halfWidth}px, 0, 0)`;
        return;
      }

      let next = current - speed;
      if (next <= 0) next += halfWidth;
      setOffset(next);
      element.style.transform = `translate3d(${-next}px, 0, 0)`;
    };

    const tick = (timestamp) => {
      const delta = Math.min(timestamp - lastTimestamp, 64);
      lastTimestamp = timestamp;

      if (!document.hidden) {
        advance(
          audienceTrackRef.current,
          delta * 0.032,
          () => audienceOffset,
          (v) => { audienceOffset = v; },
        );
        advanceLeft(
          clientTrackRef.current,
          delta * 0.045,
          () => clientOffset,
          (v) => { clientOffset = v; },
        );
      }

      animationFrameId = window.requestAnimationFrame(tick);
    };

    animationFrameId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [isInView]);

  // Duplicate testimonials for infinite scroll effect
  const duplicatedAudienceTestimonials = [...audienceTestimonials, ...audienceTestimonials];
  const duplicatedClientTestimonials = [...clientTestimonials, ...clientTestimonials];

  return (
    <section ref={sectionRef} id="testimonios" className="py-24 px-6 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto mb-20">
        <div className="text-center mb-16">
          <p
            className="inline-block rounded-full border border-black/15 bg-black/5 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-black/60 mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('testimonials.badge')}
          </p>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">{t('testimonials.title')}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('testimonials.description')}
          </p>
        </div>

        {/* Audience Testimonials */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold mb-8 text-center">{t('testimonials.audienceTitle')}</h3>
          <div className="overflow-hidden">
            <div
              ref={audienceTrackRef}
              className="flex gap-6"
              style={{ willChange: 'transform' }}
            >
              {duplicatedAudienceTestimonials.map((testimonial, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-80 bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-200 hover:border-black transition-all duration-300 hover:shadow-xl group"
              >
                <Quote size={24} className="text-gray-300 mb-4 group-hover:text-black transition-colors duration-300" />
                <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white font-bold text-sm">
                    {testimonial.author.charAt(1).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{testimonial.author}</p>
                    <p className="text-xs text-gray-500">{t('testimonials.audienceRole')}</p>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>

        {/* Client Testimonials */}
        <div>
          <h3 className="text-2xl font-bold mb-8 text-center">{t('testimonials.clientTitle')}</h3>
          <div className="overflow-hidden">
            <div
              ref={clientTrackRef}
              className="flex gap-6"
              style={{ willChange: 'transform' }}
            >
              {duplicatedClientTestimonials.map((testimonial, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-96 bg-gradient-to-br from-black to-gray-800 p-8 rounded-2xl text-white hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
              >
                <Quote size={32} className="text-white/30 mb-4 group-hover:text-white/50 transition-colors duration-300" />
                <p className="text-white/90 mb-6 leading-relaxed text-lg">"{testimonial.text}"</p>
                <div className="flex items-center gap-4 pt-4 border-t border-white/20">
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-black font-bold text-lg">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-bold">{testimonial.author}</p>
                    <p className="text-sm text-white/70">{testimonial.role}</p>
                    <p className="text-xs text-white/50 mt-1">{testimonial.subscribers}</p>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;